import { FC } from 'react';
import { DateTime } from 'luxon';
import styled from '@emotion/styled';

import { Divider } from '@material-ui/core';
import { Organization } from '@kaizenplatform/intelligence-entity';

import { PaperContent, PaperTitle, PaperTitleText, StyledPaper } from '../../primitive/Paper';
import {
  DefaultLinkPrefetchReportThreshold,
  LinkPrefetchReportThreshold,
  useLinkPrefetchReport,
} from '../../../../hooks/useLinkPrefetchReport';
import SpeculationRule from '../../../../entity/SpeculationRule';
import ReportChart from './ReportChart';
import ReportTable from './ReportTable';
import StyledLoading from './StyledLoading';
import ReportEmpty from './ReportEmpty';
import { parse } from 'qs';

type Props = {
  organization: Organization;
  speculationRule: SpeculationRule;
  toDate: DateTime;
  fromDate: DateTime;
};

const ReportContent: FC<Props> = ({ organization, fromDate, toDate, speculationRule }) => {
  const { error, loading, reportData } = useLinkPrefetchReport(organization.id, fromDate, toDate);
  if (error) {
    throw new Error(`レポートの取得に失敗しました: ${error.message}`);
  }
  if (loading || !reportData) {
    return <StyledLoading />;
  }
  if (!reportData || Object.keys(reportData.patterns).length === 0) {
    return <ReportEmpty />;
  }
  const reportThreshold: LinkPrefetchReportThreshold = buildReportThreshold();

  return (
    <Container>
      <StyledPaper style={{ marginTop: 0 }}>
        <PaperContent>
          <PaperTitle>
            <PaperTitleText>
              速度改善レポート
              <PaperTitleNote>(毎日午前3時頃に更新)</PaperTitleNote>
            </PaperTitleText>
          </PaperTitle>
          <Divider style={{ marginTop: 12, marginBottom: 16 }} />
          <ReportChart reportData={reportData} reportThreshold={reportThreshold} speculationRule={speculationRule} />
          <Divider style={{ marginTop: 24, marginBottom: 24 }} />
          <ReportTable reportData={reportData} reportThreshold={reportThreshold} speculationRule={speculationRule} />
        </PaperContent>
      </StyledPaper>
    </Container>
  );
};

const Container = styled('div')({
  width: '100%',
});

export const PaperTitleNote = styled('span')(`
  font-size: 15px;
  padding-left: 8px;
`);

function buildReportThreshold(): LinkPrefetchReportThreshold {
  const rv = Object.assign({}, DefaultLinkPrefetchReportThreshold);
  const queryParams = parse(window.location.search.slice(1)) || {};

  if (queryParams['threshold']) {
    const pv = Number(queryParams['threshold']);
    if (pv > 0) {
      rv.pv = pv;
    }
  }

  if (queryParams['lcp_threshold']) {
    const lcp = queryParams['lcp_threshold'].toString().split(',', 2);
    const lcpWarn = Number(lcp[0]);
    const lcpPoor = Number(lcp[1]);
    if (lcpWarn && lcpPoor && lcpWarn < lcpPoor) {
      rv.lcpWarn = lcpWarn;
      rv.lcpPoor = lcpPoor;
    }
  }

  if (queryParams['cls_threshold']) {
    const cls = queryParams['cls_threshold'].toString().split(',', 2);
    const clsWarn = Number(cls[0]);
    const clsPoor = Number(cls[1]);
    if (clsWarn && clsPoor && clsWarn < clsPoor) {
      rv.clsWarn = clsWarn;
      rv.clsPoor = clsPoor;
    }
  }

  return rv;
}

export default ReportContent;
